import maplibregl, { type GeoJSONSource, type LngLatBoundsLike, type Map as MapLibreMap, type StyleSpecification } from "maplibre-gl";
import bbox from "@turf/bbox";
import type { NceApiClient } from "@nce/api-client";
import { listingLayers, type MapStyleTokens } from "./layers.js";
import { parseMapState, serialiseMapState, type MapState } from "./state.js";

type ImageOverlayCoordinates = [[number, number], [number, number], [number, number], [number, number]];

const estatePlanStyle: StyleSpecification = {
  version: 8,
  name: "North Coast Estates plan canvas",
  sources: {},
  layers: [{
    id: "nce-plan-background",
    type: "background",
    paint: { "background-color": "#ddd6c9" },
  }],
};

const estateMarkerPriority: Record<string, number> = {
  zimbali: 10,
  brettenwood: 20,
  seaton: 30,
  zululami: 40,
};

function imageOverlayCoordinates(value: unknown): ImageOverlayCoordinates | null {
  if (!Array.isArray(value) || value.length !== 4) return null;
  const coordinates = value.map((position) => Array.isArray(position) && position.length === 2 && position.every((coordinate) => typeof coordinate === "number" && Number.isFinite(coordinate)) ? position as [number, number] : null);
  return coordinates.every((position): position is [number, number] => position !== null) ? coordinates as ImageOverlayCoordinates : null;
}

function publicImageUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return /^(?:https?:\/\/|\/)/i.test(value) ? value : null;
}

export interface MapControllerOptions {
  container: HTMLElement;
  api: NceApiClient;
  styleUrl: string;
  styleTokens?: MapStyleTokens;
  estateMarkerImages?: Record<string, string>;
  initialSearch?: string;
  scope?: "master" | "estate";
  onStateChange?: (state: MapState, query: string) => void;
  onListingSelect?: (reference: string) => void;
  onEstateSelect?: (slug: string) => void;
  onMapBackgroundClick?: () => void;
}

export class NceMapController {
  readonly map: MapLibreMap;
  private state: MapState;
  private selectedListingId: string | number | null = null;
  private selectedEstateId: string | number | null = null;
  private estateIdsBySlug = new Map<string, string | number>();
  private estateSlugsById = new Map<string | number, string>();
  private estateMarkerElements = new Map<string, HTMLButtonElement>();
  private estateMarkers = new Map<string, maplibregl.Marker>();
  private readonly estatePopup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 16, className: "nce-estate-map-popup" });
  private overlayLayerIds: string[] = [];
  private overlayInteractionLayerIds: string[] = [];
  private overlaySourceIds: string[] = [];
  private overlayHandlers = new Map<string, { click: () => void; enter: (event: maplibregl.MapLayerMouseEvent) => void; leave: () => void }>();
  private overlayBaseOpacities = new Map<string, number>();
  private activeOverlayLayerId: string | null = null;

  constructor(private readonly options: MapControllerOptions) {
    this.state = parseMapState(options.initialSearch ?? "");
    const planOnly = options.scope === "estate";
    options.container.dataset.mapMode = planOnly ? "estate-plan" : "geographic";
    this.map = new maplibregl.Map({ container: options.container, style: planOnly ? estatePlanStyle : options.styleUrl, center: [31.22, -29.50], zoom: 10, attributionControl: false });
    this.map.addControl(new maplibregl.NavigationControl({ visualizePitch: !planOnly, showCompass: !planOnly }), "top-right");
    if (!planOnly) this.map.addControl(new maplibregl.AttributionControl({ compact: true }));
    this.map.once("load", () => void this.load());
  }

  private async load(): Promise<void> {
    const estates = await this.options.api.mapEstates();
    for (const feature of estates.features) if (feature.id !== undefined && typeof feature.properties.slug === "string") {
      this.estateIdsBySlug.set(feature.properties.slug, feature.id);
      this.estateSlugsById.set(feature.id, feature.properties.slug);
    }
    this.map.addSource("nce-estates", { type: "geojson", data: estates as never });
    if (this.options.scope === "master") {
      this.renderEstateMarkers(estates);
      this.updateEstateMarkerDensity();
      this.map.on("zoom", () => this.updateEstateMarkerDensity());
      this.map.on("click", () => this.options.onMapBackgroundClick?.());
    } else {
      const listings = await this.options.api.mapListings(this.filters());
      this.map.addSource("nce-listings", { type: "geojson", data: listings as never, cluster: true, clusterMaxZoom: 14, clusterRadius: 48 });
      for (const layer of listingLayers(this.options.styleTokens)) this.map.addLayer(layer);
      this.map.on("click", "nce-listing-points", (event) => { const feature = event.features?.[0]; const reference = feature?.properties?.reference as string | undefined; if (feature?.id !== undefined && reference) this.selectListing(feature.id, reference); });
      this.map.on("click", "nce-listing-clusters", (event) => { const feature = event.features?.[0]; const clusterId = Number(feature?.properties?.cluster_id); if (!feature || !Number.isFinite(clusterId)) return; const source = this.map.getSource("nce-listings") as GeoJSONSource; source.getClusterExpansionZoom(clusterId).then((zoom) => { if (feature.geometry.type === "Point") this.map.easeTo({ center: feature.geometry.coordinates as [number, number], zoom }); }).catch(() => undefined); });
      for (const layer of ["nce-listing-points", "nce-listing-clusters"]) {
        this.map.on("mouseenter", layer, () => { this.map.getCanvas().style.cursor = "pointer"; });
        this.map.on("mouseleave", layer, () => { this.map.getCanvas().style.cursor = ""; });
      }
    }
    if (this.options.scope === "master") this.options.container.dataset.overlayCount = "0";
    if (!this.state.estate && !this.state.bounds && estates.features.length) {
      const coverage = bbox(estates as never);
      if (coverage.every(Number.isFinite)) this.map.fitBounds([[coverage[0], coverage[1]], [coverage[2], coverage[3]]], { padding: 64, duration: 0, maxZoom: 11 });
    }
    this.map.on("moveend", () => { const bounds = this.map.getBounds(); this.patchState({ bounds: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()] }); });
    if (this.state.estate) await this.focusEstate(this.state.estate);
  }

  private filters() { return { ...(this.state.estate ? { estate: this.state.estate } : {}), ...(this.state.development ? { development: this.state.development } : {}), ...(this.state.kind ? { kind: this.state.kind } : {}), ...(this.state.propertyType ? { propertyType: this.state.propertyType } : {}), ...(this.state.minPrice !== undefined ? { minPrice: this.state.minPrice } : {}), ...(this.state.maxPrice !== undefined ? { maxPrice: this.state.maxPrice } : {}), ...(this.state.bedrooms !== undefined ? { bedrooms: this.state.bedrooms } : {}), ...(this.state.bathrooms !== undefined ? { bathrooms: this.state.bathrooms } : {}), ...(this.state.status ? { status: this.state.status } : {}), ...(this.state.feature ? { feature: this.state.feature } : {}), ...(this.state.onShow ? { onShow: true } : {}), ...(this.state.bounds ? { bounds: this.state.bounds.join(",") } : {}) }; }

  private patchState(patch: Partial<MapState>): void { this.state = { ...this.state, ...patch }; this.options.onStateChange?.(this.state, serialiseMapState(this.state)); }

  private renderEstateMarkers(estates: Awaited<ReturnType<NceApiClient["mapEstates"]>>): void {
    for (const marker of this.estateMarkers.values()) marker.remove();
    this.estateMarkers.clear();
    this.estateMarkerElements.clear();

    const features = [...estates.features].sort((left, right) => {
      const leftSlug = typeof left.properties?.slug === "string" ? left.properties.slug : "";
      const rightSlug = typeof right.properties?.slug === "string" ? right.properties.slug : "";
      return (estateMarkerPriority[leftSlug] ?? 0) - (estateMarkerPriority[rightSlug] ?? 0);
    });

    for (const feature of features) {
      const slug = typeof feature.properties?.slug === "string" ? feature.properties.slug : "";
      if (!slug) continue;
      const name = String(feature.properties?.shortName ?? feature.properties?.name ?? "Estate");
      const area = String(feature.properties?.area ?? "North Coast");
      const accent = String(feature.properties?.accent ?? this.options.styleTokens?.estateLine ?? "#10C7C7");
      const bounds = bbox(feature as never);
      if (!bounds.every(Number.isFinite)) continue;
      const publishedCentre = feature.properties?.centre;
      const centre: [number, number] = Array.isArray(publishedCentre)
        && publishedCentre.length === 2
        && publishedCentre.every((coordinate) => typeof coordinate === "number" && Number.isFinite(coordinate))
        ? publishedCentre as [number, number]
        : [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2];

      const button = document.createElement("button");
      button.type = "button";
      button.className = "nce-estate-marker";
      button.dataset.estateSlug = slug;
      button.setAttribute("aria-label", `Open ${name} information`);
      button.title = `${name} — ${area}`;
      button.style.setProperty("--nce-estate-marker-accent", accent);

      const portrait = document.createElement("span");
      portrait.className = "nce-estate-marker__portrait";
      const imageUrl = this.options.estateMarkerImages?.[slug];
      if (imageUrl) {
        const image = document.createElement("img");
        image.src = imageUrl;
        image.alt = "";
        image.loading = "eager";
        portrait.append(image);
      } else {
        const monogram = name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
        portrait.append(Object.assign(document.createElement("span"), { textContent: monogram }));
      }

      const label = document.createElement("span");
      label.className = "nce-estate-marker__label";
      const title = document.createElement("strong");
      title.textContent = name;
      const context = document.createElement("small");
      context.textContent = area || "Estate information";
      label.append(title, context);
      button.append(portrait, label);
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        this.selectEstateMarker(slug);
        if (this.options.onEstateSelect) this.options.onEstateSelect(slug);
        else void this.focusEstate(slug);
      });

      // Estate markers are geographic references. Keep the portrait centred on
      // the published point at every zoom instead of applying screen-pixel
      // offsets that can visually move coastal estates kilometres offshore.
      const marker = new maplibregl.Marker({ element: button, anchor: "center" }).setLngLat(centre).addTo(this.map);
      this.estateMarkerElements.set(slug, button);
      this.estateMarkers.set(slug, marker);
    }
  }

  private updateEstateMarkerDensity(): void {
    const zoom = this.map.getZoom();
    this.options.container.dataset.markerDensity = zoom < 13 ? "compact" : zoom < 14.5 ? "medium" : "expanded";
  }

  private selectEstateMarker(slug: string): void {
    for (const [markerSlug, element] of this.estateMarkerElements) {
      if (markerSlug === slug) element.setAttribute("data-selected", "");
      else element.removeAttribute("data-selected");
    }
  }

  clearEstateSelection(): void {
    this.selectEstateMarker("");
    if (this.selectedEstateId !== null) this.map.setFeatureState({ source: "nce-estates", id: this.selectedEstateId }, { selected: false });
    this.selectedEstateId = null;
    delete this.state.estate;
    delete this.state.listing;
    this.options.onStateChange?.(this.state, serialiseMapState(this.state));
  }

  async focusEstate(slug: string): Promise<void> {
    const [estate, overlays] = await Promise.all([
      this.options.api.estate(slug),
      this.options.scope === "estate" ? this.options.api.mapOverlays(slug) : Promise.resolve([]),
    ]);
    this.state = { ...this.state, estate: slug };
    this.selectEstateMarker(slug);
    delete this.state.listing;
    this.options.onStateChange?.(this.state, serialiseMapState(this.state));
    if (this.selectedEstateId !== null) this.map.setFeatureState({ source: "nce-estates", id: this.selectedEstateId }, { selected: false });
    this.selectedEstateId = this.estateIdsBySlug.get(slug) ?? null;
    if (this.selectedEstateId !== null) this.map.setFeatureState({ source: "nce-estates", id: this.selectedEstateId }, { selected: true });
    if ("estate" === this.options.scope) this.showOnlyEstate(slug);
    if (estate.bounds) this.map.fitBounds(estate.bounds as LngLatBoundsLike, { padding: 52, duration: matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 900, maxZoom: 15 });
    else if (estate.centre) this.map.easeTo({ center: estate.centre.coordinates, zoom: estate.camera.zoom, pitch: estate.camera.pitch, bearing: estate.camera.bearing, duration: matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 900 });
    if (this.options.scope !== "master") {
      const listings = await this.options.api.mapListings(this.filters());
      (this.map.getSource("nce-listings") as GeoJSONSource | undefined)?.setData(listings as never);
    }
    if (this.options.scope === "estate") this.renderOverlays(overlays);
  }

  private showOnlyEstate(slug: string): void {
    const estate = ["==", ["get", "slug"], slug];
    if (this.map.getLayer("nce-estate-fill")) this.map.setFilter("nce-estate-fill", ["all", ["==", ["geometry-type"], "Polygon"], estate] as never);
    if (this.map.getLayer("nce-estate-line")) this.map.setFilter("nce-estate-line", ["all", ["==", ["geometry-type"], "Polygon"], estate] as never);
    if (this.map.getLayer("nce-estate-points")) this.map.setFilter("nce-estate-points", ["all", ["==", ["geometry-type"], "Point"], estate] as never);
    if (this.map.getLayer("nce-estate-label")) this.map.setFilter("nce-estate-label", estate as never);
  }

  private renderOverlays(overlays: Awaited<ReturnType<NceApiClient["mapOverlays"]>>): void {
    this.options.container.dataset.overlayCount = "0";
    const planCoordinates: Array<[number, number]> = [];
    for (const [layerId, handlers] of this.overlayHandlers) if (this.map.getLayer(layerId)) {
      this.map.off("click", layerId, handlers.click);
      this.map.off("mouseenter", layerId, handlers.enter);
      this.map.off("mouseleave", layerId, handlers.leave);
    }
    this.overlayHandlers.clear();
    this.overlayBaseOpacities.clear();
    this.activeOverlayLayerId = null;
    for (const layer of [...this.overlayInteractionLayerIds, ...this.overlayLayerIds].reverse()) if (this.map.getLayer(layer)) this.map.removeLayer(layer);
    for (const source of this.overlaySourceIds) if (this.map.getSource(source)) this.map.removeSource(source);
    this.overlayLayerIds = []; this.overlayInteractionLayerIds = []; this.overlaySourceIds = [];
    for (const overlay of overlays) {
      if (overlay.layerType !== "raster") continue;
      const contexts = Array.isArray(overlay.sourceConfig.contexts) ? overlay.sourceConfig.contexts.filter((value): value is string => typeof value === "string") : [];
      if (contexts.length && !contexts.includes(this.options.scope ?? "master")) continue;
      const sourceId = `nce-overlay-source-${overlay.id}`; const layerId = `nce-overlay-${overlay.id}`;
      const hitSourceId = `${sourceId}-hit`; const hitLayerId = `${layerId}-hit`;
      const imageUrl = publicImageUrl(overlay.sourceConfig.url);
      const imageCoordinates = imageOverlayCoordinates(overlay.sourceConfig.coordinates);
      if (overlay.sourceConfig.kind === "image" && imageUrl && imageCoordinates) {
        this.map.addSource(sourceId, { type: "image", url: imageUrl, coordinates: imageCoordinates });
        planCoordinates.push(...imageCoordinates);
      } else {
        const tiles = Array.isArray(overlay.sourceConfig.tiles) ? overlay.sourceConfig.tiles.filter((value): value is string => typeof value === "string" && /^https:\/\//i.test(value)) : [];
        if (!tiles.length) continue;
        const bounds = Array.isArray(overlay.sourceConfig.bounds) && overlay.sourceConfig.bounds.length === 4 && overlay.sourceConfig.bounds.every((value) => typeof value === "number") ? overlay.sourceConfig.bounds as [number, number, number, number] : undefined;
        this.map.addSource(sourceId, { type: "raster", tiles, tileSize: Number(overlay.sourceConfig.tileSize) || 256, ...(bounds ? { bounds } : {}) });
      }
      const configuredOpacity = typeof overlay.styleConfig.opacity === "number" ? overlay.styleConfig.opacity : .72;
      const baseOpacity = this.options.scope === "master" ? Math.min(configuredOpacity, .48) : 1;
      this.map.addLayer({ id: layerId, type: "raster", source: sourceId, minzoom: overlay.minZoom ?? 0, maxzoom: overlay.maxZoom ?? 24, paint: { "raster-opacity": baseOpacity, "raster-opacity-transition": { duration: 180, delay: 0 }, "raster-fade-duration": 350 } }, this.map.getLayer("nce-listing-clusters") ? "nce-listing-clusters" : undefined);
      let interactionLayerId = layerId;
      if (this.options.scope === "master" && imageCoordinates) {
        this.map.addSource(hitSourceId, {
          type: "geojson",
          data: {
            type: "Feature",
            properties: { overlayId: overlay.id },
            geometry: { type: "Polygon", coordinates: [[...imageCoordinates, imageCoordinates[0]]] },
          },
        });
        this.map.addLayer({
          id: hitLayerId,
          type: "fill",
          source: hitSourceId,
          minzoom: overlay.minZoom ?? 0,
          maxzoom: overlay.maxZoom ?? 24,
          paint: { "fill-color": "#000000", "fill-opacity": .001 },
        });
        interactionLayerId = hitLayerId;
        this.overlaySourceIds.push(hitSourceId);
        this.overlayInteractionLayerIds.push(hitLayerId);
      }
      this.overlayBaseOpacities.set(layerId, baseOpacity);
      if (this.options.scope === "master") {
      const overlaySlug = overlay.estateId !== null ? this.estateSlugsById.get(overlay.estateId) : undefined;
      const handlePlanClick = () => {
        if (this.options.scope === "master" && this.activeOverlayLayerId && this.activeOverlayLayerId !== layerId) return;
        const slug = overlaySlug ?? this.state.estate;
        if (slug) this.options.onEstateSelect?.(slug);
      };
      const handlePlanEnter = (event: maplibregl.MapLayerMouseEvent) => {
        this.map.getCanvas().style.cursor = "pointer";
        this.activeOverlayLayerId = layerId;
        if (this.options.scope === "master") this.emphasiseOverlay(layerId);
        const card = document.createElement("div");
        card.className = "nce-map-popup-card";
        const name = document.createElement("strong");
        name.textContent = overlay.name.replace(/ published estate plan$/i, "");
        const instruction = document.createElement("span");
        instruction.textContent = this.options.scope === "master" ? "Actual estate plan · click to open" : "Actual estate plan · click for estate links";
        card.append(name, instruction);
        this.estatePopup.setLngLat(event.lngLat).setDOMContent(card).addTo(this.map);
      };
      const handlePlanLeave = () => {
        if (this.activeOverlayLayerId === layerId) this.activeOverlayLayerId = null;
        this.map.getCanvas().style.cursor = "";
        this.estatePopup.remove();
        if (this.options.scope === "master") this.restoreOverlayOpacities();
      };
      this.map.on("click", interactionLayerId, handlePlanClick);
      this.map.on("mouseenter", interactionLayerId, handlePlanEnter);
      this.map.on("mouseleave", interactionLayerId, handlePlanLeave);
      this.overlayHandlers.set(interactionLayerId, { click: handlePlanClick, enter: handlePlanEnter, leave: handlePlanLeave });
      }
      this.overlaySourceIds.push(sourceId); this.overlayLayerIds.push(layerId);
    }
    if (this.options.scope === "estate" && planCoordinates.length) {
      const longitudes = planCoordinates.map(([longitude]) => longitude);
      const latitudes = planCoordinates.map(([, latitude]) => latitude);
      this.map.fitBounds([
        [Math.min(...longitudes), Math.min(...latitudes)],
        [Math.max(...longitudes), Math.max(...latitudes)],
      ], { padding: 34, duration: matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 700, maxZoom: 17 });
    }
    this.options.container.dataset.overlayCount = String(this.overlayLayerIds.length);
    this.options.container.dispatchEvent(new CustomEvent("nce:map-overlays", { detail: { count: this.overlayLayerIds.length, overlays: overlays.map((overlay) => ({ name: overlay.name, sourceLabel: overlay.sourceConfig.sourceLabel, reviewState: overlay.sourceConfig.reviewState })) } }));
  }

  private emphasiseOverlay(activeLayerId: string): void {
    for (const layerId of this.overlayLayerIds) if (this.map.getLayer(layerId)) this.map.setPaintProperty(layerId, "raster-opacity", layerId === activeLayerId ? .86 : .16);
  }

  private restoreOverlayOpacities(): void {
    for (const layerId of this.overlayLayerIds) if (this.map.getLayer(layerId)) this.map.setPaintProperty(layerId, "raster-opacity", this.overlayBaseOpacities.get(layerId) ?? .48);
  }

  private selectListing(id: string | number, reference: string): void {
    if (this.selectedListingId !== null) this.map.setFeatureState({ source: "nce-listings", id: this.selectedListingId }, { selected: false });
    this.selectedListingId = id; this.map.setFeatureState({ source: "nce-listings", id }, { selected: true }); this.patchState({ listing: reference }); this.options.onListingSelect?.(reference);
  }

  async setFilters(patch: Partial<MapState>): Promise<void> {
    this.patchState(patch);
    if (this.options.scope === "master") return;
    const listings = await this.options.api.mapListings(this.filters());
    (this.map.getSource("nce-listings") as GeoJSONSource | undefined)?.setData(listings as never);
  }
  destroy(): void {
    for (const [layerId, handlers] of this.overlayHandlers) if (this.map.getLayer(layerId)) {
      this.map.off("click", layerId, handlers.click);
      this.map.off("mouseenter", layerId, handlers.enter);
      this.map.off("mouseleave", layerId, handlers.leave);
    }
    this.overlayHandlers.clear();
    this.map.remove();
  }
}
